"use client";

import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  FINANCIAL_WORK_FILTER_PARAM,
  FINANCIAL_WORK_LOAD_FAIL_PARAM,
  FINANCIAL_WORK_NAV_PARAM,
  FINANCIAL_WORK_PANEL_PARAM,
  FINANCIAL_WORK_REV_ID_PARAM,
  FINANCIAL_WORK_REV_PARAM,
  clearFinancialWorkIntent,
  consumeFinancialWorkOpenRecord,
  financialWorkObjectHrefPatch,
  normalizeFinancialWorkSearch,
  parseObjectRefFromSearch,
  parseRevisionFromSearch,
  readFinancialWorkIntent,
  recoverOperationByKey,
  resolveActionOperationKey,
  resolveFinancialWorkNav,
  saveFinancialWorkOpenRecord,
  slipTransferLabel,
} from "@/lib/financialWork";
import {
  BILLIN_RECEIVABLE_WORKER_ID,
} from "@/lib/billinConstants";
import { BILLIN_SCROLL_TO_RECEIVABLE } from "@/lib/billinGoRoutes";
import { postFinancialWorkAction } from "@/lib/moneyOperations";
import {
  fetchReceivableDebtsDetails,
  getReceivableOrderId,
  resolveOwnerUserId,
  type ReceivableListItem,
} from "@/lib/pendingOrders";
import { fetchWorkspaceTags } from "@/lib/workspaceAvatarTags";
import type { Workspace } from "@/lib/api";
import type {
  ActionDirective,
  BacklogChildRow,
  FinancialWorkNavId,
  LiffFinancialWorkCard,
  ObjectRef,
  OperationRecoveryState,
  WorkQueueProjection,
} from "@/lib/financialWork/types";
import {
  useFinancialWorkDesktop,
  useFinancialWorkWideRail,
  FINANCIAL_WORK_MOBILE_SCROLL_CLASS,
  FINANCIAL_WORK_DESKTOP_SCROLL_CLASS,
} from "@/lib/financialWork/layout";
import WorkerSidebar from "@/app/components/WorkerSidebar";
import { WorkerMarketplaceSection } from "@/app/components/WorkerMarketplaceSection";
import { WorkerMyTeamSection } from "@/app/components/WorkerMyTeamSection";
import { ServiceSwitcher } from "@/app/components/ServiceSwitcher";
import { WorkspaceSwitcher } from "@/app/components/WorkspaceSwitcher";
import { ProfileMagicServiceBanner } from "@/app/components/profile-magic/ProfileMagicServiceBanner";
import { useOrdersContext } from "@/context/OrdersContext";
import { useFinancialWorkSearch } from "@/app/financial-work/FinancialWorkSearchContext";
import type { Worker } from "@/app/components/workercard/WorkerCard";
import {
  DEFAULT_WORKSPACE_AVATAR,
  resolveWorkspaceAvatarFromWorkspace,
  resolveWorkspaceImageDisplayUrl,
} from "@/lib/workspaceAvatar";
import { preloadWorkspaceAvatars } from "@/lib/workspaceImageCache";
import { emitWorkspaceSelected, readLiveSearch } from "@/lib/lastWorkspaceStorage";
import { showWorkspaceSwitchToast } from "@/lib/workspaceSwitchToast";
import { continuePendingBillinGo } from "@/lib/billinGoRoutes";
import { navigateHomeAfterWorkspacePick } from "@/lib/workspacePickHint";
import { ArrowLeft } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { useFinancialWorkMutationGate } from "@/hooks/useFinancialWorkMutationGate";
import { subscribeAuthSession } from "@/lib/authSession";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { hasSigninAuthSession } from "@/lib/signinPendingAuth";
import {
  isGuestQueueNoSession,
  isLoggedInQueueOutsider,
  workspaceIdInList,
} from "@/lib/financialWork/queueAccess";
import { useFinancialWorkQueue } from "@/hooks/useFinancialWorkQueue";
import { useBatchRosterEnrichment } from "@/hooks/useBatchRosterEnrichment";
import { useFinancialWorkFeed, invalidateFinancialWorkFeedCache } from "@/hooks/useFinancialWorkFeed";
import { useExpiredQueueNotice } from "@/hooks/useExpiredQueueNotice";
import { FEED_PAGE_SIZE, enrichFeedCardsWithQueueRoster, expandOpenBatchMatesInProjection, feedCardOpensRecordSurface, feedToQueueProjection, mergeClosedQueueCards, mergeOpenReceivableQueueCards } from "@/lib/financialWork/feedClient";
import { notifyIncomeMoneyChanged } from "@/lib/incomeMoneyRefresh";
import { useFinancialWorkPreparing } from "@/app/financial-work/FinancialWorkPreparingContext";
import { ReceivableRecordDrawer } from "@/app/artifacts/table/ReceivableRecordDrawer";
import { ExpiredQueueNoticeModal } from "@/app/financial-work/ExpiredQueueNoticeModal";
import { showToast } from "@/app/components/ui/Toast";
import {
  FinancialWorkAccessDeniedPanel,
  FinancialWorkDetailModal,
  FinancialWorkPrimaryNav,
  FinancialWorkQueuePreparing,
  FinancialWorkQueueView,
  FinancialWorkSourcesPlaceholder,
  buildFinancialWorkHref,
  parseQueueFilterId,
  type QueueFilterId,
} from "./FinancialWorkViews";

function childStatusLabel(status: BacklogChildRow["status"]): string {
  switch (status) {
    case "open":
      return "ค้าง";
    case "waiting_slip":
      return "รอสลิป";
    case "paid":
      return "จ่ายแล้ว";
    case "needs_review":
      return "ต้องตรวจ";
    default:
      return status;
  }
}

function childCardFromRow(
  parent: LiffFinancialWorkCard,
  row: BacklogChildRow,
  workspaceId?: string | null,
): LiffFinancialWorkCard {
  const incomeRecordId =
    row.dataNav?.workerId === "bill-lin-income"
      ? row.dataNav.recordId?.trim() || ""
      : "";
  const ws = (workspaceId || "").trim();
  const imageUrl =
    incomeRecordId && ws
      ? `/api/v1/transactions/${encodeURIComponent(incomeRecordId)}/image?workspace_id=${encodeURIComponent(ws)}`
      : undefined;
  const hasMatchedSlip = Boolean(incomeRecordId) || row.status === "paid";
  const parentMatch = (parent.evidenceItems ?? []).find(
    (item) =>
      item.matchedObjectId === row.objectRef.id ||
      (incomeRecordId && item.dataNav?.recordId === incomeRecordId) ||
      item.label.includes(row.label),
  );
  const transferor =
    row.slipPartyName?.trim() ||
    parentMatch?.transferorLabel?.trim() ||
    "";
  const siblingTransferMinor = (parent.backlogChildren ?? []).reduce(
    (largest, child) => {
      const text = `${child.label} ${child.slipLabel || ""}`;
      if (
        !/สลิป|LINE/i.test(text) ||
        (transferor && !text.includes(transferor))
      ) {
        return largest;
      }
      return Math.max(
        largest,
        child.paidAmountMinor ??
          child.originalAmountMinor ??
          child.remainingAmountMinor ??
          0,
      );
    },
    0,
  );
  const transferAmountMinor = Math.max(
    parentMatch?.transferAmountMinor ?? 0,
    siblingTransferMinor,
  );
  const slipText = (row.slipLabel || "").trim();
  const amountDetail =
    parentMatch?.detail ||
    (/[0-9]/.test(slipText) ? slipText : undefined);
  return {
    objectRef: row.objectRef,
    projectionRevision: parent.projectionRevision,
    audience: parent.audience,
    title: row.label,
    subtitle: row.slipLabel,
    amountSummary:
      row.remainingAmountMinor != null
        ? {
            currency: row.currency || "THB",
            primaryAmountMinor: row.remainingAmountMinor,
            basis: "remaining",
          }
        : undefined,
    surfaceState:
      row.status === "waiting_slip"
        ? "waiting"
        : row.status === "paid"
          ? "closed"
          : "needs_action",
    workPlacement: { bucket: "waiting", rank: 99 },
    requirementStatus: {
      complete: row.status === "paid",
      summary: row.slipLabel,
    },
    nextStep: {
      label: row.status === "waiting_slip" ? "รอสลิปเข้า" : "ติดตามยอดค้าง",
    },
    contextRows: [
      { key: "customer", label: "ลูกค้า", value: row.label },
      { key: "batch", label: "รอบ", value: parent.title },
      { key: "slip", label: "สลิป", value: row.slipLabel || childStatusLabel(row.status) },
      {
        key: "status",
        label: "สถานะ",
        value: row.status === "paid" ? "จ่ายครบแล้ว" : childStatusLabel(row.status),
      },
    ],
    consequenceRows: [],
    evidenceSummary: {
      complete: hasMatchedSlip || row.status === "paid" ? 1 : 0,
      missing: hasMatchedSlip
        ? 0
        : row.status === "waiting_slip" || row.status === "open"
          ? 1
          : 0,
      needsReview: row.status === "needs_review" ? 1 : 0,
    },
    evidenceItems: [
      parentMatch
        ? {
            ...parentMatch,
            label: slipTransferLabel(
              row.slipPartyName ||
                (parentMatch.label.startsWith("สลิปโอนของ ")
                  ? parentMatch.label.slice("สลิปโอนของ ".length)
                  : undefined),
              row.label,
            ),
            matchedObjectId: row.objectRef.id,
            counterpartLabel:
              parentMatch.counterpartLabel || `ยอด${row.label}`,
            counterpartDetail: parentMatch.counterpartDetail || amountDetail,
            occurredAt: parentMatch.occurredAt || row.occurredAt,
            originalAmountMinor:
              parentMatch.originalAmountMinor ?? row.originalAmountMinor,
            outstandingAmountMinor: parentMatch.outstandingAmountMinor,
            matchedAmountMinor:
              parentMatch.matchedAmountMinor ?? row.paidAmountMinor,
            transferAmountMinor:
              transferAmountMinor > 0 ? transferAmountMinor : undefined,
            remainingAmountMinor:
              parentMatch.remainingAmountMinor ?? row.remainingAmountMinor,
          }
        : {
            id: `ev-${row.objectRef.id}-slip`,
            label: slipTransferLabel(row.slipPartyName, row.label),
            status: hasMatchedSlip
              ? "complete"
              : row.status === "needs_review"
                ? "needs_review"
                : "missing",
            detail: amountDetail,
            dataNav: row.dataNav,
            imageUrl: row.slipImageUrl || imageUrl,
            matchedObjectId: row.objectRef.id,
            counterpartLabel: `ยอด${row.label}`,
            counterpartDetail: amountDetail,
            occurredAt: row.occurredAt,
            originalAmountMinor: row.originalAmountMinor,
            matchedAmountMinor: row.paidAmountMinor,
            transferAmountMinor:
              transferAmountMinor > 0 ? transferAmountMinor : undefined,
            remainingAmountMinor: row.remainingAmountMinor,
          },
    ],
    backlogChildren: parent.backlogChildren,
    timelineSummary: parent.timelineSummary,
    timelineEvents: [
      {
        id: `tl-${row.objectRef.id}-open`,
        label: `เปิดยอด ${row.label}`,
        occurredAtLabel:
          parent.timelineSummary?.occurredAt ||
          parent.timelineEvents?.[0]?.occurredAtLabel ||
          "รอบนี้",
      },
      {
        id: `tl-${row.objectRef.id}-now`,
        label:
          row.status === "waiting_slip"
            ? "รอสลิปเข้า"
            : row.status === "paid"
              ? "ปิดยอดแล้ว"
              : "ยังค้าง",
        occurredAtLabel: "ล่าสุด",
      },
    ],
    availableActions: [],
    detailLink: row.detailLink || "",
    freshness: "current",
  };
}

function matchReceivableItem(
  items: ReceivableListItem[],
  ids: Array<string | null | undefined>,
): ReceivableListItem | null {
  const want = new Set(
    ids
      .flatMap((raw) => {
        const value = String(raw ?? "").trim();
        if (!value) return [];
        const stripped = value.startsWith("redis:") ? value.slice(6) : value;
        return stripped && stripped !== value ? [value, stripped] : [value];
      })
      .filter(Boolean),
  );
  if (want.size === 0) return null;
  for (const item of items) {
    const candidates = [
      getReceivableOrderId(item),
      item.receivableId,
      item.mergeKey,
      item.key,
      item.paymentId,
    ];
    if (candidates.some((value) => value && want.has(value))) return item;
  }
  return null;
}

type Props = {
  workspaceId: string | null;
  workspaceName?: string | null;
  workspace?: Workspace | null;
  workspaces?: Workspace[];
  isLoadingWorkspaces?: boolean;
  serviceId?: string | null;
  /** Legacy 5-tab / data views — preserved under 「ข้อมูล」 */
  dataView: React.ReactNode;
  /** Optional accounting entry under 「รอบบัญชี」 */
  periodsView?: React.ReactNode;
  showPeriodsNav?: boolean;
  /** false = ยังไม่จ้าง Bill-Lin — แสดงแชทจ้างในเปลือก fw-shell ชุดเดียว */
  hired?: boolean;
  hireView?: React.ReactNode;
  onWorkspaceIdChange?: (workspaceId: string) => void;
};

export function FinancialWorkShell({
  workspaceId,
  workspaceName,
  workspace = null,
  workspaces = [],
  isLoadingWorkspaces = false,
  serviceId = null,
  dataView,
  periodsView,
  showPeriodsNav = false,
  hired = true,
  hireView,
  onWorkspaceIdChange,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  const [taggedWorkspace, setTaggedWorkspace] = useState<Workspace | null>(
    workspace,
  );
  const [workspaceHeaderReady, setWorkspaceHeaderReady] = useState(false);
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const workspaceNameRef = useRef(workspaceName);
  workspaceNameRef.current = workspaceName;
  const headerReadyForIdRef = useRef<string | null>(null);

  const headerWorkspaceId = (workspaceId || workspace?.id || "").trim();

  useEffect(() => {
    if (!workspace || workspace.id !== headerWorkspaceId) return;
    setTaggedWorkspace((current) => {
      if (current?.id !== workspace.id) return workspace;
      return {
        ...workspace,
        tags: current.tags?.length ? current.tags : workspace.tags,
      };
    });
  }, [workspace, headerWorkspaceId]);

  useEffect(() => {
    if (!headerWorkspaceId) {
      headerReadyForIdRef.current = null;
      setWorkspaceHeaderReady(false);
      setTaggedWorkspace(null);
      return;
    }
    if (headerReadyForIdRef.current === headerWorkspaceId) return;

    let cancelled = false;
    setWorkspaceHeaderReady(false);

    void (async () => {
      const latest = workspaceRef.current;
      const name = (
        workspaceNameRef.current ||
        latest?.name ||
        ""
      ).trim();
      let next: Workspace = latest?.id === headerWorkspaceId
        ? latest
        : { id: headerWorkspaceId, name };
      try {
        const tags = await fetchWorkspaceTags(headerWorkspaceId, latest?.tags);
        if (cancelled) return;
        if (tags.length > 0) {
          next = { ...next, id: headerWorkspaceId, tags };
        }
        setTaggedWorkspace((current) =>
          current?.id === headerWorkspaceId && current.tags?.length
            ? { ...next, tags: current.tags }
            : next,
        );
        await preloadWorkspaceAvatars([next]);
      } catch {
        setTaggedWorkspace((current) =>
          current?.id === headerWorkspaceId ? current : next,
        );
      }
      if (cancelled) return;
      headerReadyForIdRef.current = headerWorkspaceId;
      setWorkspaceHeaderReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [headerWorkspaceId]);

  useEffect(() => {
    const name = (
      workspaceName ||
      workspace?.name ||
      taggedWorkspace?.name ||
      ""
    ).trim();
    if (headerReadyForIdRef.current === headerWorkspaceId && name) {
      setWorkspaceHeaderReady(true);
    }
  }, [headerWorkspaceId, workspaceName, workspace?.name, taggedWorkspace?.name]);

  const ownerUserId = useMemo(
    () => resolveOwnerUserId(taggedWorkspace),
    [taggedWorkspace],
  );

  const fw = searchParams?.get(FINANCIAL_WORK_NAV_PARAM) ?? null;
  const objectRef = useMemo(
    () =>
      parseObjectRefFromSearch(
        new URLSearchParams(searchParams?.toString() ?? ""),
      ),
    [searchParams],
  );
  const lineEntryRevision = useMemo(
    () =>
      parseRevisionFromSearch(
        new URLSearchParams(searchParams?.toString() ?? ""),
      ),
    [searchParams],
  );
  const urlFocusPanel = useMemo(() => {
    const raw = searchParams?.get(FINANCIAL_WORK_PANEL_PARAM);
    if (raw === "outstanding" || raw === "children") return raw;
    return null;
  }, [searchParams]);
  /** Set synchronously on click so chase does not flash the aggregate detail first. */
  const [localFocusPanel, setLocalFocusPanel] = useState<
    "outstanding" | "children" | null
  >(null);
  const focusPanel = localFocusPanel ?? urlFocusPanel;
  const [childOverlay, setChildOverlay] =
    useState<LiffFinancialWorkCard | null>(null);
  const [moneyCache, setMoneyCache] = useState<{
    items: ReceivableListItem[];
    announcement: ReceivableListItem[];
    slip: ReceivableListItem[];
  } | null>(null);
  const [moneyItem, setMoneyItem] = useState<ReceivableListItem | null>(null);
  const [moneyBusy, setMoneyBusy] = useState(false);
  const queueFilter = useMemo(
    () => parseQueueFilterId(searchParams?.get(FINANCIAL_WORK_FILTER_PARAM)),
    [searchParams],
  );

  const activeNav = useMemo(
    () =>
      resolveFinancialWorkNav({
        fw,
        workerId: searchParams?.get("workerId") ?? null,
        recordId: searchParams?.get("recordId") ?? null,
        highlight: searchParams?.get("highlight") ?? null,
        scrollTo: searchParams?.get("scrollTo") ?? null,
        modal: searchParams?.get("modal") ?? null,
        objectRef: objectRef ? `${objectRef.kind}:${objectRef.id}` : null,
      }),
    [fw, objectRef, searchParams],
  );

  const hireSource = searchParams?.get("source");
  const workMode =
    hired || hireSource === "order" || hireSource === "my-team";
  const urlWorkspaceId = searchParams?.get("workspaceId")?.trim() || null;
  // Prefer live shell workspace (may update before router.replace finishes).
  // Keep preparing cover until URL catches up so hired shops never flash hire list.
  const workspaceNavSynced =
    !workspaceId || !urlWorkspaceId || workspaceId === urlWorkspaceId;

  const {
    orders,
    marketplaceWorkers,
    workersFromOrders,
    loadingMarketplace,
    loadingOrderWorkers,
    loadingOrders,
    isInitialLoading,
  } = useOrdersContext();

  const [authReady, setAuthReady] = useState(false);
  const [hasViewer, setHasViewer] = useState(false);
  const [guestSettled, setGuestSettled] = useState(false);
  useEffect(() => {
    const sync = () => {
      setHasViewer(Boolean(getUserIdFromAuthToken()?.trim()));
      setAuthReady(true);
    };
    sync();
    return subscribeAuthSession(sync);
  }, []);

  useEffect(() => {
    if (!authReady || hasViewer || hasSigninAuthSession()) {
      setGuestSettled(false);
      return;
    }
    const timer = window.setTimeout(() => setGuestSettled(true), 250);
    return () => window.clearTimeout(timer);
  }, [authReady, hasViewer]);

  const hireStatusKnown = authReady && !loadingOrders;
  const harnessNoAuth = searchParams?.get("fwHarnessNoAuth") === "1";
  // Own shop is in `workspaces` even while buyer orders are still empty —
  // !hired must not flash ไม่มีสิทธิ์ on the owner's workspace.
  const ownsCurrentWorkspace = workspaceIdInList(workspaceId, workspaces);
  const loggedInOutsider = isLoggedInQueueOutsider({
    workMode,
    hireStatusKnown,
    hasViewer,
    hired,
    harnessNoAuth,
    isLoadingWorkspaces,
    workspaceId,
    workspaces,
  });
  const guestNoSession = isGuestQueueNoSession({
    workMode,
    hireStatusKnown: hireStatusKnown && (guestSettled || harnessNoAuth),
    hasViewer,
    hasSessionHint: hasSigninAuthSession(),
  });
  const accessDenied = loggedInOutsider || guestNoSession;
  const fwSearch = useFinancialWorkSearch();
  const feedBucket = queueFilter ?? "needs_action";
  const feedQuery = fwSearch?.searchQuery ?? "";
  const [feedPage, setFeedPage] = useState(0);
  const [debouncedFeedQuery, setDebouncedFeedQuery] = useState(feedQuery);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedFeedQuery(feedQuery), 250);
    return () => window.clearTimeout(timer);
  }, [feedQuery]);
  useEffect(() => {
    setFeedPage(0);
  }, [feedBucket, debouncedFeedQuery, workspaceId]);

  // Badge counts must load on ข้อมูล / sources too — the queue list still
  // mounts only on the work tab. Keep search/paging off that background fetch
  // so a ข้อมูล search does not shrink the งานเงิน number.
  const workFeedActive =
    Boolean(workspaceId) && workMode && hireStatusKnown;
  const workListActive = workFeedActive && activeNav === "work";
  const { feed, unavailable: feedUnavailable, loading: feedLoading, error: feedError, errorKind: feedErrorKind, reload: reloadFeed } = useFinancialWorkFeed({
    workspaceId,
    ownerUserId,
    enabled: workFeedActive,
    refetchWhenVisible: false,
    accessDenied,
    page: workListActive ? feedPage : 0,
    pageSize: FEED_PAGE_SIZE,
    bucket: workListActive ? feedBucket : "needs_action",
    q: workListActive ? debouncedFeedQuery : "",
  });
  const { projection: queueProjection, loading: queueLoadingRaw, error: queueError, errorKind: queueErrorKind, reload: reloadQueue } = useFinancialWorkQueue({
    workspaceId,
    ownerUserId,
    enabled: workFeedActive,
    refetchWhenVisible: false,
    accessDenied,
  });
  const expiredNotice = useExpiredQueueNotice({
    ownerUserId,
    workspaceId,
    enabled: workFeedActive && !accessDenied,
  });
  const projection = useMemo(() => {
    let base: WorkQueueProjection | null = null;
    if (feed && !feedUnavailable) {
      base = feedToQueueProjection(feed, {
        canonicalTruth: queueProjection?.canonicalTruth,
        navigation: feed.navigation ?? queueProjection?.navigation,
        actorCapabilities:
          feed.actorCapabilities ?? queueProjection?.actorCapabilities,
        actorLabel: queueProjection?.actorLabel,
        scopeLabel: queueProjection?.scopeLabel,
      });
      if (queueProjection) {
        base = enrichFeedCardsWithQueueRoster(base, queueProjection);
        base = mergeOpenReceivableQueueCards(base, queueProjection);
        base = mergeClosedQueueCards(base, queueProjection);
      }
    } else {
      base = queueProjection;
    }
    if (!base) return base;
    return expandOpenBatchMatesInProjection(base);
  }, [feed, feedUnavailable, queueProjection]);
  const loading = feedUnavailable ? queueLoadingRaw : feedLoading && !feed;
  const queueCountsLoading =
    workFeedActive &&
    Boolean((feedLoading && !feed) || (workListActive && feedLoading && Boolean(feed)));
  const error = feedUnavailable ? queueError : feedError;
  const errorKind = feedUnavailable ? queueErrorKind : feedErrorKind;
  const reload = useCallback((opts?: { silent?: boolean }) => {
    invalidateFinancialWorkFeedCache(workspaceId);
    void reloadFeed(
      opts?.silent ? { silent: true, force: true } : { force: true },
    );
    void reloadQueue(opts);
    notifyIncomeMoneyChanged({ reason: "financial_work_reload" });
    void expiredNotice.refresh();
  }, [expiredNotice.refresh, reloadFeed, reloadQueue, workspaceId]);
  const queueLoading =
    (activeNav === "work" && loading) ||
    (workMode && !hireStatusKnown) ||
    (workMode && isLoadingWorkspaces && !ownsCurrentWorkspace) ||
    (workMode && !hasViewer && !guestNoSession);
  const showAccessDenied =
    hireStatusKnown &&
    !queueLoading &&
    (accessDenied || errorKind === "unauthorized");
  const forceLoadFail =
    hireStatusKnown &&
    !showAccessDenied &&
    searchParams?.get(FINANCIAL_WORK_LOAD_FAIL_PARAM) === "1";
  const isDesktop = useFinancialWorkDesktop();
  const isWideRail = useFinancialWorkWideRail();
  const [hireChatOpen, setHireChatOpen] = useState(false);
  const expiredNotice = useExpiredQueueNotice({
    ownerUserId,
    workspaceId,
    enabled: workMode && hireStatusKnown && !showAccessDenied && !accessDenied,
  });

  const workCount = useMemo(() => {
    if (typeof projection?.tabCounts?.all === "number") {
      return projection.tabCounts.all;
    }
    if (!projection?.buckets?.length) return null;
    return projection.buckets.reduce(
      (sum, bucket) => sum + (bucket.count ?? 0),
      0,
    );
  }, [projection]);
  const hideWorkCountBadge =
    forceLoadFail || showAccessDenied || Boolean(error);
  const displayedWorkCount = hideWorkCountBadge ? null : workCount;
  const workCountLoading =
    !hideWorkCountBadge && queueCountsLoading && displayedWorkCount == null;
  const showSourcesNav = Boolean(
    projection?.navigation?.sourcesAndSharing?.visible ||
      (projection?.navigation as { sourcesAndSharing?: { visible?: boolean } } | undefined)
        ?.sourcesAndSharing?.visible,
  );
  const workspaceAvatar = taggedWorkspace
    ? resolveWorkspaceAvatarFromWorkspace({
        imageUrl: taggedWorkspace.imageUrl,
        tags: taggedWorkspace.tags,
        resolveImageUrl: resolveWorkspaceImageDisplayUrl,
      })
    : DEFAULT_WORKSPACE_AVATAR;

  const preparing = useFinancialWorkPreparing();
  const setPreparingReady = preparing?.setReady;
  const setOverviewReady = preparing?.setOverviewReady;
  /** Once hire UI has settled for a workspace, ignore marketplace refetch flicker. */
  const hireSettledForRef = useRef<string | null>(null);

  useEffect(() => {
    hireSettledForRef.current = null;
    setHireChatOpen(false);
  }, [workspaceId]);

  // Mount desktop hire chat under the preparing cover, then lift the cover
  // once chat is open — avoids an empty-frame flash between load and chat.
  useEffect(() => {
    if (workMode) return;
    if (!isDesktop) return;
    if (!(serviceId || "").trim()) return;
    setHireChatOpen(true);
  }, [workMode, isDesktop, serviceId, workspaceId]);

  const contentReady = useMemo(() => {
    if (!workspaceNavSynced) return false;
    if (!workspaceHeaderReady) return false;
    if (workMode) {
      if (isLoadingWorkspaces || loadingOrders || isInitialLoading) return false;
      if (!workspaceId) return false;
      if (activeNav === "work" && loading) return false;
      return true;
    }
    const hireSettleKey = workspaceId || "no-ws";
    // Keep cover from bouncing back when services/orders refetch after first paint.
    if (hireSettledForRef.current === hireSettleKey) return true;
    // First hire paint: initial cache only — not loadingMarketplace refetch.
    if (isLoadingWorkspaces || isInitialLoading) return false;
    if (isDesktop) {
      const hasWorkers =
        marketplaceWorkers.length > 0 || workersFromOrders.length > 0;
      if (hasWorkers && !hireChatOpen) return false;
    }
    return true;
  }, [
    workspaceNavSynced,
    workspaceHeaderReady,
    workMode,
    isLoadingWorkspaces,
    loadingOrders,
    isInitialLoading,
    workspaceId,
    activeNav,
    loading,
    isDesktop,
    marketplaceWorkers.length,
    workersFromOrders.length,
    hireChatOpen,
  ]);

  useEffect(() => {
    setPreparingReady?.(contentReady);
  }, [setPreparingReady, contentReady]);

  useEffect(() => {
    if (workMode) return;
    if (!contentReady) return;
    hireSettledForRef.current = workspaceId || "no-ws";
  }, [workMode, contentReady, workspaceId]);

  // Overview summaries only exist on the work tab. Waiting for them on data/sources
  // (or before the queue mounts Overview) leaves the preparing cover stuck forever.
  useEffect(() => {
    if (!workMode || activeNav !== "work") {
      setOverviewReady?.(true);
      return;
    }
    setOverviewReady?.(false);
  }, [setOverviewReady, workMode, workspaceId, activeNav]);

  // Queue surfaces that never mount FinancialWorkOverview — stop waiting.
  useEffect(() => {
    if (!workMode || activeNav !== "work") return;
    if (loading) return;
    if (
      error ||
      !projection ||
      projection.serverProjectionMissing ||
      projection.freshness === "unavailable"
    ) {
      setOverviewReady?.(true);
    }
  }, [setOverviewReady, workMode, activeNav, loading, error, projection]);

  const hiredWorkerIds = useMemo(
    () =>
      orders
        .filter((order) => {
          if (!workspaceId) return true;
          return (order.workspaceId || order.workspace?.id) === workspaceId;
        })
        .map((order) => order.serviceId),
    [orders, workspaceId],
  );
  const activeHireTab =
    hireSource === "order" || hireSource === "my-team"
      ? "my-team"
      : "marketplace";
  const selectedHireWorker = useMemo(() => {
    const sid = (serviceId || "").trim();
    if (!sid) return null;
    return (
      workersFromOrders.find((worker) => worker.id === sid) ??
      marketplaceWorkers.find((worker) => worker.id === sid) ??
      null
    );
  }, [marketplaceWorkers, serviceId, workersFromOrders]);
  const serviceHeaderReady = Boolean(
    selectedHireWorker ||
      !(serviceId || "").trim() ||
      !(loadingMarketplace || loadingOrderWorkers || isLoadingWorkspaces),
  );
  const handleSelectHireWorker = useCallback(
    (worker: Worker, source?: "my-team" | "marketplace") => {
      const params = new URLSearchParams(
        readLiveSearch() || searchParams?.toString() || "",
      );
      params.set("serviceId", worker.id);
      if (source === "my-team") {
        if (worker.workspaceId) params.set("workspaceId", worker.workspaceId);
        else if (workspaceId) params.set("workspaceId", workspaceId);
        params.set("source", "order");
      } else {
        if (workspaceId) params.set("workspaceId", workspaceId);
        params.set("source", "marketplace");
      }
      if (source === "my-team") {
        router.push(`/?${params.toString()}`, { scroll: false });
        return;
      }
      setHireChatOpen(true);
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname || `/?${qs}`, {
        scroll: false,
      });
    },
    [pathname, router, searchParams, workspaceId],
  );

  // If desktop hire has no serviceId yet, pick the first available worker.
  useEffect(() => {
    if (workMode) return;
    if (!isDesktop) return;
    if (!workspaceNavSynced) return;
    if (loadingMarketplace || loadingOrderWorkers || isInitialLoading) return;
    if ((serviceId || "").trim()) return;
    const firstMyTeam = workersFromOrders[0];
    const firstMarket = marketplaceWorkers[0];
    const first = firstMyTeam ?? firstMarket;
    if (!first) return;
    handleSelectHireWorker(
      first,
      firstMyTeam ? "my-team" : "marketplace",
    );
  }, [
    workMode,
    isDesktop,
    workspaceNavSynced,
    loadingMarketplace,
    loadingOrderWorkers,
    isInitialLoading,
    serviceId,
    workersFromOrders,
    marketplaceWorkers,
    handleSelectHireWorker,
  ]);

  const [recovery, setRecovery] = useState<OperationRecoveryState>({
    kind: "idle",
  });
  /** Open immediately on card click; URL object param stays source of truth for deep links. */
  const [localDetailCard, setLocalDetailCard] =
    useState<LiffFinancialWorkCard | null>(null);
  /** Close immediately even if clearing `object` from the URL lags. */
  const [detailDismissed, setDetailDismissed] = useState(false);
  /** Ignore card clicks that leak through while the modal is closing. */
  const closeLockUntilRef = useRef(0);
  /** Last action for same-key recovery retry (timeout / unknown status). */
  const lastActionRef = useRef<ActionDirective | null>(null);
  const intentRestoreAttemptsRef = useRef(0);

  const replaceSearch = useCallback(
    (patch: Record<string, string | null>) => {
      const liveSearch =
        typeof window !== "undefined"
          ? window.location.search.replace(/^\?/, "")
          : (searchParams?.toString() ?? "");
      const href = buildFinancialWorkHref(liveSearch, patch);
      router.replace(`${pathname}${href === "?" ? "" : href}`, {
        scroll: false,
      });
    },
    [pathname, router, searchParams],
  );

  // After login/binding interruption: restore opaque object until it sticks in the URL.
  // Do not clear intent until `object` is present — the embedded ข้อมูล table can
  // history.replaceState worker/filter params and briefly race the restore.
  useEffect(() => {
    if (objectRef) {
      intentRestoreAttemptsRef.current = 0;
      clearFinancialWorkIntent();
      return;
    }
    if (activeNav !== "work") return;

    const intent = readFinancialWorkIntent();
    if (!intent?.objectRef) return;
    if (!intent.returnPath.startsWith(pathname)) return;
    if (intentRestoreAttemptsRef.current >= 8) return;
    intentRestoreAttemptsRef.current += 1;

    setDetailDismissed(false);
    replaceSearch({
      [FINANCIAL_WORK_NAV_PARAM]: intent.nav ?? "work",
      ...financialWorkObjectHrefPatch(intent.objectRef),
    });
  }, [activeNav, objectRef, pathname, replaceSearch]);

  useEffect(() => {
    const liveSearch = readLiveSearch() || searchParams?.toString() || "";
    if (!liveSearch) return;
    const liveWid = new URLSearchParams(liveSearch).get("workspaceId")?.trim() || "";
    const paramWid = searchParams?.get("workspaceId")?.trim() || "";
    if (liveWid && paramWid && liveWid !== paramWid) return;
    const { next, stripped, migrated } = normalizeFinancialWorkSearch(liveSearch);
    if ((!stripped.length && !migrated) || next === liveSearch) return;
    router.replace(next ? `${pathname}?${next}` : pathname, { scroll: false });
  }, [pathname, router, searchParams]);

  const setNav = useCallback(
    (nav: FinancialWorkNavId) => {
      setDetailDismissed(true);
      setLocalDetailCard(null);
      setLocalFocusPanel(null);
      setChildOverlay(null);
      clearFinancialWorkIntent();
      replaceSearch({
        [FINANCIAL_WORK_NAV_PARAM]: nav,
        ...financialWorkObjectHrefPatch(null),
      });
    },
    [replaceSearch],
  );

  useEffect(() => {
    if (!projection || loading) return;
    if (activeNav !== "sources" || showSourcesNav) return;
    replaceSearch({ [FINANCIAL_WORK_NAV_PARAM]: "work" });
  }, [activeNav, loading, projection, replaceSearch, showSourcesNav]);

  const openDataNav = useCallback(
    (nav: {
      workerId: string;
      recordId?: string;
      scrollTo?: string;
      highlight?: string;
    }) => {
      const recordId = nav.recordId?.trim() || nav.highlight?.trim() || null;
      const highlight = nav.highlight?.trim() || nav.recordId?.trim() || null;
      if (nav.workerId !== BILLIN_RECEIVABLE_WORKER_ID) {
        setDetailDismissed(true);
        setLocalDetailCard(null);
        setLocalFocusPanel(null);
        setChildOverlay(null);
        clearFinancialWorkIntent();
        if (recordId && nav.workerId) {
          saveFinancialWorkOpenRecord({
            workerId: nav.workerId,
            recordId,
            highlight: highlight ?? recordId,
          });
        }
        replaceSearch({
          [FINANCIAL_WORK_NAV_PARAM]: "data",
          ...financialWorkObjectHrefPatch(null),
          workerId: nav.workerId,
          recordId,
          scrollTo: nav.scrollTo ?? null,
          highlight,
        });
        return;
      }

      closeLockUntilRef.current = Date.now() + 800;
      setDetailDismissed(true);
      setLocalDetailCard(null);
      setLocalFocusPanel(null);
      setChildOverlay(null);
      if (!workspaceId || !ownerUserId) {
        showToast("เปิดแก้ยอดไม่สำเร็จ — ไม่พบร้านนี้", "error");
        return;
      }
      if (moneyBusy) return;
      setMoneyBusy(true);
      void (async () => {
        try {
          let items = moneyCache?.items;
          let announcement = moneyCache?.announcement;
          let slip = moneyCache?.slip;
          if (!items || !announcement || !slip) {
            const details = await fetchReceivableDebtsDetails(ownerUserId, {
              workspaceId,
            });
            items = details.lists.items;
            announcement = details.lists.byKind.announcement;
            slip = details.lists.byKind.slip;
            setMoneyCache({ items, announcement, slip });
          }
          const item = matchReceivableItem(items, [
            recordId,
            highlight,
            objectRef?.id,
          ]);
          if (!item) {
            showToast("ไม่พบยอดค้างนี้ในข้อมูลปัจจุบัน", "error");
            return;
          }
          setMoneyItem(item);
        } catch (err) {
          showToast(
            err instanceof Error ? err.message : "เปิดแก้ยอดไม่สำเร็จ",
            "error",
          );
        } finally {
          setMoneyBusy(false);
        }
      })();
    },
    [
      moneyBusy,
      moneyCache,
      objectRef?.id,
      ownerUserId,
      replaceSearch,
      workspaceId,
    ],
  );

  useEffect(() => {
    setMoneyCache(null);
    setMoneyItem(null);
  }, [workspaceId]);

  const refreshMoneyDrawer = useCallback(async () => {
    if (!ownerUserId || !workspaceId) {
      void reload({ silent: true });
      return;
    }
    const details = await fetchReceivableDebtsDetails(ownerUserId, {
      force: true,
      workspaceId,
    });
    const next = {
      items: details.lists.items,
      announcement: details.lists.byKind.announcement,
      slip: details.lists.byKind.slip,
    };
    setMoneyCache(next);
    setMoneyItem((current) => {
      if (!current) return current;
      return (
        matchReceivableItem(next.items, [
          getReceivableOrderId(current),
          current.receivableId,
          current.mergeKey,
          current.key,
        ]) ?? current
      );
    });
    void reload({ silent: true });
  }, [ownerUserId, reload, workspaceId]);

  const selectedCardBase: LiffFinancialWorkCard | null = useMemo(() => {
    // Card click updates localDetailCard before the URL `object` param catches up.
    const ref = localDetailCard?.objectRef ?? objectRef;
    if (!ref || !projection) return localDetailCard;
    const match = (card: LiffFinancialWorkCard) =>
      card.objectRef.kind === ref.kind && card.objectRef.id === ref.id;
    for (const bucket of projection.buckets) {
      const hit = bucket.cards.find(match);
      if (hit) return hit;
    }
    const pageHit = projection.pageCards?.find(match);
    if (pageHit) return pageHit;
    return localDetailCard;
  }, [localDetailCard, objectRef, projection]);
  const selectedCard = useBatchRosterEnrichment(
    selectedCardBase,
    queueProjection,
    workspaceId,
    ownerUserId,
  );
  const selectedCardRef = useRef(selectedCard);
  selectedCardRef.current = selectedCard;

  const openCard = useCallback(
    (
      card: LiffFinancialWorkCard,
      options?: { panel?: "outstanding" | "children" | null },
    ) => {
      if (Date.now() < closeLockUntilRef.current) return;
      if (feedCardOpensRecordSurface(card)) {
        const nav = card.availableActions.find(
          (action) => action.actionType === "open_data",
        )?.dataNav;
        if (nav?.workerId) {
          openDataNav({
            workerId: nav.workerId,
            recordId: nav.recordId,
            scrollTo: nav.scrollTo,
            highlight: nav.highlight,
          });
          return;
        }
      }
      setDetailDismissed(false);
      setLocalFocusPanel(options?.panel ?? null);
      setLocalDetailCard(card);
      replaceSearch({
        [FINANCIAL_WORK_NAV_PARAM]: "work",
        ...financialWorkObjectHrefPatch(card.objectRef, card.projectionRevision),
        [FINANCIAL_WORK_PANEL_PARAM]: options?.panel ?? null,
      });
    },
    [openDataNav, replaceSearch],
  );

  const openChildRef = useCallback(
    (ref: ObjectRef) => {
      const parent = selectedCardRef.current;
      if (projection) {
        for (const bucket of projection.buckets) {
          const hit = bucket.cards.find(
            (c) =>
              c.objectRef.kind === ref.kind && c.objectRef.id === ref.id,
          );
          if (hit) {
            setChildOverlay(hit);
            return;
          }
        }
      }
      const row = parent?.backlogChildren?.find(
        (c) => c.objectRef.kind === ref.kind && c.objectRef.id === ref.id,
      );
      if (row && parent) {
        setChildOverlay(childCardFromRow(parent, row, workspaceId));
      }
    },
    [projection, workspaceId],
  );

  const handleAction = useCallback(
    async (action: ActionDirective, card?: LiffFinancialWorkCard) => {
      if (action.actionType === "open_detail") {
        if (card) openCard(card);
        return;
      }
      if (action.actionType === "open_outstanding") {
        if (card) openCard(card, { panel: "outstanding" });
        return;
      }
      if (action.actionType === "open_data") {
        const firstChild = card?.outstanding?.items?.[0];
        const fallbackId =
          action.objectRef.kind === "receivable"
            ? action.objectRef.id.replace(/^redis:/, "")
            : firstChild?.dataNav?.recordId?.trim() ||
              firstChild?.objectRef.id.replace(/^redis:/, "") ||
              undefined;
        const nav = action.dataNav ?? {
          workerId: BILLIN_RECEIVABLE_WORKER_ID,
          scrollTo: BILLIN_SCROLL_TO_RECEIVABLE,
          highlight: fallbackId,
          recordId: fallbackId,
        };
        openDataNav({
          ...nav,
          recordId: nav.recordId?.trim() || fallbackId,
          highlight: nav.highlight?.trim() || fallbackId,
        });
        return;
      }

      lastActionRef.current = action;

      if (action.actionType === "copy_reminder") {
        if (!workspaceId) return;
        const operationKey = resolveActionOperationKey(action);
        try {
          await postFinancialWorkAction({
            workspaceId,
            actionType: action.actionType,
            operationKey,
            objectKind: action.objectRef.kind,
            objectId: action.objectRef.id,
            requiredRevision: action.requiredRevision,
            actorId: ownerUserId,
          });
        } catch {
          /* clipboard already happened; copy is not a money mutation */
        }
        return;
      }

      if (!canMutate) {
        setRecovery({
          kind: "failed",
          operationKey: action.operationKey,
          message: blockedReason,
          retrySafeWithSameKey: true,
        });
        return;
      }

      const operationKey = resolveActionOperationKey(action);
      setRecovery({ kind: "submitting", operationKey });

      const isFwAction =
        action.actionType === "acknowledge" ||
        operationKey.startsWith("fw:");
      if (isFwAction && workspaceId) {
        try {
          const posted = await postFinancialWorkAction({
            workspaceId,
            actionType: action.actionType,
            operationKey,
            objectKind: action.objectRef.kind,
            objectId: action.objectRef.id,
            requiredRevision: action.requiredRevision,
            actorId: ownerUserId,
          });
          if (posted.conflict) {
            setRecovery({
              kind: "failed",
              operationKey,
              message:
                posted.message?.trim() || "การ์ดเก่า — กำลังโหลดใหม่",
              retrySafeWithSameKey: true,
            });
            void reload();
            return;
          }
          setRecovery({
            kind: "committed",
            operationKey,
            status: posted.operation_status || "applied",
          });
          void reload();
          return;
        } catch (err) {
          const result = await recoverOperationByKey(operationKey, {
            maxAttempts: 3,
            onState: setRecovery,
          });
          if (result.kind === "committed") {
            void reload();
            return;
          }
          setRecovery({
            kind: "failed",
            operationKey,
            message: err instanceof Error ? err.message : "ทำรายการไม่สำเร็จ",
            retrySafeWithSameKey: true,
          });
          return;
        }
      }

      const result = await recoverOperationByKey(operationKey, {
        maxAttempts: 3,
        onState: setRecovery,
      });
      if (result.kind === "committed") {
        void reload();
      }
    },
    [
      blockedReason,
      canMutate,
      openCard,
      openDataNav,
      reload,
      workspaceId,
    ],
  );

  const handleRetryRecovery = useCallback(() => {
    const action = lastActionRef.current;
    if (!action) return;
    const operationKey = resolveActionOperationKey(action);
    setRecovery({ kind: "processing", operationKey, message: "กำลังตรวจผลรายการ…" });
    void (async () => {
      const result = await recoverOperationByKey(operationKey, {
        maxAttempts: 3,
        onState: setRecovery,
      });
      if (result.kind === "committed") {
        void reload();
      }
    })();
  }, [reload]);

  const ackLineStaleEntry = useCallback(() => {
    const card = selectedCardRef.current;
    replaceSearch({
      [FINANCIAL_WORK_REV_PARAM]: FINANCIAL_WORK_REV_ID_PARAM,
      [FINANCIAL_WORK_REV_ID_PARAM]: card
        ? String(card.projectionRevision)
        : null,
    });
  }, [replaceSearch]);

  const backFromChase = useCallback(() => {
    setLocalFocusPanel(null);
    replaceSearch({
      [FINANCIAL_WORK_PANEL_PARAM]: null,
    });
  }, [replaceSearch]);

  const refreshOutstanding = useCallback(() => {
    void reload();
  }, [reload]);

  const detailTargetRef: ObjectRef | null =
    localDetailCard?.objectRef ?? objectRef ?? null;
  const detailCardReady = Boolean(
    detailTargetRef &&
      selectedCard &&
      selectedCard.objectRef.kind === detailTargetRef.kind &&
      selectedCard.objectRef.id === detailTargetRef.id,
  );
  const showDetailModal =
    activeNav === "work" &&
    !detailDismissed &&
    (Boolean(objectRef) || Boolean(localDetailCard));
  const detailPending =
    showDetailModal &&
    !error &&
    !detailCardReady &&
    (loading || !projection || Boolean(localDetailCard));

  const [pendingSlipsLoading, setPendingSlipsLoading] = useState(false);
  const pendingSlipCardKey = selectedCard
    ? `${selectedCard.objectRef.kind}:${selectedCard.objectRef.id}`
    : "";
  const selectedCardWaitingForSlip = Boolean(
    selectedCard &&
      selectedCard.objectRef.kind === "receivable" &&
      (selectedCard.workPlacement?.bucket === "waiting" ||
        selectedCard.surfaceState === "waiting") &&
      !selectedCard.requirementStatus?.complete,
  );
  useEffect(() => {
    if (
      !showDetailModal ||
      !pendingSlipCardKey ||
      !selectedCardWaitingForSlip ||
      !ownerUserId ||
      !workspaceId
    ) {
      setPendingSlipsLoading(false);
      return;
    }
    if (moneyCache?.slip && moneyCache?.announcement) {
      setPendingSlipsLoading(false);
      return;
    }
    let cancelled = false;
    setPendingSlipsLoading(true);
    void fetchReceivableDebtsDetails(ownerUserId, { workspaceId })
      .then((details) => {
        if (cancelled) return;
        setMoneyCache({
          items: details.lists.items,
          announcement: details.lists.byKind.announcement,
          slip: details.lists.byKind.slip,
        });
      })
      .catch(() => {
        /* Advice panel falls back to empty pending-slip list. */
      })
      .finally(() => {
        if (!cancelled) setPendingSlipsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    showDetailModal,
    pendingSlipCardKey,
    selectedCardWaitingForSlip,
    ownerUserId,
    workspaceId,
    moneyCache?.slip,
    moneyCache?.announcement,
  ]);

  const closeChild = useCallback(() => {
    // Keep parent from receiving the same click after this overlay unmounts.
    closeLockUntilRef.current = Date.now() + 700;
    setChildOverlay(null);
  }, []);

  const dismissDetail = useCallback(() => {
    closeLockUntilRef.current = Date.now() + 800;
    setRecovery({ kind: "idle" });
    setDetailDismissed(true);
    setLocalFocusPanel(null);
    setChildOverlay(null);
    // Keep the current card mounted so bounce-out does not swap to the empty state.
    setLocalDetailCard((current) => current ?? selectedCardRef.current);
    clearFinancialWorkIntent();
    replaceSearch({
      [FINANCIAL_WORK_NAV_PARAM]: "work",
      ...financialWorkObjectHrefPatch(null),
      [FINANCIAL_WORK_PANEL_PARAM]: null,
    });
  }, [replaceSearch]);

  const closeDetail = useCallback(() => {
    if (childOverlay) {
      closeChild();
      return;
    }
    if (Date.now() < closeLockUntilRef.current) return;
    dismissDetail();
  }, [childOverlay, closeChild, dismissDetail]);

  useEffect(() => {
    if (recovery.kind !== "committed") return;
    showToast("บันทึกสำเร็จ");
    dismissDetail();
  }, [recovery.kind, dismissDetail]);

  const handleDetailExited = useCallback(() => {
    setLocalDetailCard(null);
    setLocalFocusPanel(null);
    setChildOverlay(null);
  }, []);

  const workspaceAvatarSize = isDesktop ? 32 : 36;
  const workspaceLabel = (
    workspaceName ||
    taggedWorkspace?.name ||
    "ร้าน"
  ).trim();

  const patchHomeSearch = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(
        readLiveSearch() || searchParams?.toString() || "",
      );
      mutate(params);
      router.replace(`/?${params.toString()}`, { scroll: false });
    },
    [router, searchParams],
  );

  const handleSelectWorkspace = useCallback(
    (id: string) => {
      setIsWorkspaceDropdownOpen(false);
      if (id !== workspaceId) {
        const picked = workspaces.find((item) => item.id === id);
        showWorkspaceSwitchToast(picked?.name);
      }
      emitWorkspaceSelected(id);
      onWorkspaceIdChange?.(id);
      if (
        continuePendingBillinGo(id, (href) => {
          router.replace(href, { scroll: false });
        })
      ) {
        return;
      }
      navigateHomeAfterWorkspacePick(
        (href) => router.replace(href, { scroll: false }),
        id,
        serviceId || searchParams?.get("serviceId"),
        orders,
      );
    },
    [
      onWorkspaceIdChange,
      orders,
      router,
      searchParams,
      serviceId,
      workspaceId,
      workspaces,
    ],
  );

  const handleOpenWorkspaceSettings = useCallback(() => {
    if (!workspaceId) return;
    patchHomeSearch((params) => {
      params.set("workspaceId", workspaceId);
      params.set("setting", "true");
      params.delete("newworkspace");
    });
  }, [patchHomeSearch, workspaceId]);

  const handleAddWorkspace = useCallback(() => {
    patchHomeSearch((params) => {
      params.delete("setting");
      params.set("newworkspace", "true");
    });
  }, [patchHomeSearch]);

  // Marketplace / unhired: hire list in the main column only below 900.
  // From 900–1440 service picks live beside workspace; at 1440+ WorkerSidebar has the list.
  const showHireList = Boolean(
    !workMode && !isDesktop && workspaceNavSynced,
  );

  const workspaceHeader = (
    <WorkspaceSwitcher
      variant={isWideRail ? "rail" : isDesktop ? "top" : "mobile"}
      workspaces={workspaces}
      selectedWorkspaceId={workspaceId}
      workspaceLabel={workspaceLabel}
      workspaceAvatar={workspaceAvatar}
      isLoading={isLoadingWorkspaces}
      ready={workspaceHeaderReady}
      avatarSize={workspaceAvatarSize}
      iconSize={isDesktop ? 16 : 18}
      open={isWorkspaceDropdownOpen}
      onOpenChange={(open) => {
        setIsWorkspaceDropdownOpen(open);
        if (open) setIsServiceDropdownOpen(false);
      }}
      onSelect={handleSelectWorkspace}
      onOpenSettings={handleOpenWorkspaceSettings}
      onAddWorkspace={handleAddWorkspace}
    />
  );

  // Hired / source=order is work chrome — no Bill-Lin service dropdown.
  const showServiceHeader = !workMode;

  const serviceHeader = showServiceHeader ? (
    <ServiceSwitcher
      variant={isDesktop ? "top" : "mobile"}
      selectedWorker={selectedHireWorker}
      activeTab={activeHireTab}
      hiredWorkerIds={hiredWorkerIds}
      searchQuery={fwSearch?.searchQuery ?? ""}
      isLoading={isLoadingWorkspaces}
      ready={serviceHeaderReady}
      open={isServiceDropdownOpen}
      onOpenChange={(open) => {
        setIsServiceDropdownOpen(open);
        if (open) setIsWorkspaceDropdownOpen(false);
      }}
      onSelect={handleSelectHireWorker}
    />
  ) : null;

  const hireViewNode =
    !workMode && hireView
      ? !isWideRail && isValidElement(hireView)
        ? cloneElement(
            hireView as ReactElement<{ hideHeader?: boolean }>,
            { hideHeader: true },
          )
        : hireView
      : null;

  const hireListSidebar = (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4">
      <WorkerMyTeamSection
        activeTab={activeHireTab}
        selectedWorker={hireChatOpen ? selectedHireWorker : null}
        handleWorkerClick={handleSelectHireWorker}
        searchQuery={fwSearch?.searchQuery ?? ""}
      />
      <WorkerMarketplaceSection
        activeTab={activeHireTab}
        selectedWorker={hireChatOpen ? selectedHireWorker : null}
        handleWorkerClick={handleSelectHireWorker}
        searchQuery={fwSearch?.searchQuery ?? ""}
        hiredWorkerIds={hiredWorkerIds}
      />
    </div>
  );

  const mainColumn = (
    <>
      {isDesktop && !isWideRail ? (
        <div className="flex h-14 shrink-0 items-stretch border-b border-aera-lightgray">
          {workspaceHeader}
          {serviceHeader}
          {workMode ? (
            <div className="flex min-w-0 flex-1 items-stretch">
              <FinancialWorkPrimaryNav
                active={activeNav}
                onChange={setNav}
                showPeriods={showPeriodsNav}
                showSources={showSourcesNav}
                workCount={displayedWorkCount}
                workCountLoading={workCountLoading}
                variant="top"
              />
              {(activeNav === "work" || activeNav === "data") &&
              workspaceId ? (
                <div
                  className="ml-auto flex max-w-[min(22rem,42%)] shrink-0 items-center px-4 empty:hidden"
                  data-testid="fw-matching-readiness"
                >
                  <ProfileMagicServiceBanner
                    workspaceId={workspaceId}
                    compact
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {!isDesktop && !showHireList ? (
        <>
          {workspaceHeader}
          {workMode &&
          (activeNav === "work" || activeNav === "data") &&
          workspaceId ? (
            <div
              className="mx-4 mt-2 empty:hidden"
              data-testid="fw-matching-readiness"
            >
              <ProfileMagicServiceBanner
                workspaceId={workspaceId}
                compact
                className="rounded-2xl border border-aera-lightgray bg-aera-white px-4 py-3"
              />
            </div>
          ) : null}
        </>
      ) : null}

      {showHireList ? (
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-aera-white">
          {!isDesktop ? (
            <div className="shrink-0">{workspaceHeader}</div>
          ) : null}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {hireListSidebar}
          </div>
          {hireChatOpen && hireViewNode ? (
            <div
              className="fixed inset-0 z-50 flex flex-col bg-aera-white"
              aria-hidden={false}
            >
              <div className="flex h-14 shrink-0 items-center gap-2 border-b border-aera-lightgray px-2">
                <AeraButton
                  type="button"
                  variant="transparent"
                  paddingSize="none"
                  onClick={() => setHireChatOpen(false)}
                  aria-label="กลับไปเลือกบริการ"
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-aera-charcoal"
                >
                  <ArrowLeft size={20} strokeWidth={2.5} aria-hidden />
                </AeraButton>
                <span className="min-w-0 truncate text-sm font-semibold text-aera-charcoal">
                  {selectedHireWorker?.name ?? "แชท"}
                </span>
              </div>
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                {hireViewNode}
              </div>
            </div>
          ) : null}
        </div>
      ) : !workMode && hireViewNode && hireChatOpen ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {hireViewNode}
        </div>
      ) : !workMode ? (
        <div className="min-h-0 flex-1 bg-aera-white" />
      ) : (
        <>
      {activeNav === "work" ? (
        <div
          className={
            isDesktop
              ? isWideRail
                ? FINANCIAL_WORK_DESKTOP_SCROLL_CLASS
                : "flex min-h-full w-full min-w-0 flex-1 flex-col"
              : FINANCIAL_WORK_MOBILE_SCROLL_CLASS
          }
        >
          <FinancialWorkQueueView
            projection={projection}
            loading={forceLoadFail ? false : queueLoading}
            countsLoading={queueCountsLoading}
            error={forceLoadFail ? error || "โหลดข้อมูลไม่สำเร็จ" : error}
            errorKind={
              showAccessDenied
                ? "unauthorized"
                : forceLoadFail
                  ? "generic"
                  : errorKind
            }
            workspaceId={workspaceId}
            workspaceName={workspaceLabel}
            serviceId={serviceId}
            ownerUserId={ownerUserId}
            initialFilter={queueFilter ?? undefined}
            onFilterChange={(next: QueueFilterId) =>
              replaceSearch({
                [FINANCIAL_WORK_FILTER_PARAM]: next === "needs_action" ? null : next,
              })
            }
            pageIndex={projection?.page ? feedPage : undefined}
            onPageChange={projection?.page ? setFeedPage : undefined}
            onOpenCard={openCard}
            onAction={(action, card) => void handleAction(action, card)}
            onOpenData={() => setNav("data")}
            onRetry={() => {
              if (forceLoadFail) {
                replaceSearch({ [FINANCIAL_WORK_LOAD_FAIL_PARAM]: null });
              }
              void reload();
            }}
          />
        </div>
      ) : null}

      <FinancialWorkDetailModal
        open={showDetailModal}
        card={detailPending ? null : selectedCard}
        loading={detailPending || pendingSlipsLoading}
        missing={!selectedCard && !detailPending}
        recovery={recovery}
        onClose={closeDetail}
        onExited={handleDetailExited}
        onAction={(action) =>
          void handleAction(action, selectedCard ?? undefined)
        }
        onRetryRecovery={handleRetryRecovery}
        lineEntryRevision={lineEntryRevision}
        onAckLineStale={ackLineStaleEntry}
        onReloadProjection={refreshOutstanding}
        focusPanel={focusPanel}
        onOpenChild={openChildRef}
        onBackFromChase={backFromChase}
        childOverlay={childOverlay}
        onCloseChild={closeChild}
        onOpenDataNav={openDataNav}
        workspaceId={workspaceId}
        ownerUserId={ownerUserId}
        pendingSlipItems={moneyCache?.slip ?? []}
        pendingAnnouncementItems={moneyCache?.announcement ?? []}
        pendingSlipsLoading={pendingSlipsLoading}
        onPendingSlipMatched={() => {
          void refreshMoneyDrawer();
          void reload({ silent: true });
        }}
      />

      <ReceivableRecordDrawer
        open={Boolean(moneyItem)}
        onOpenChange={(open) => {
          if (!open) setMoneyItem(null);
        }}
        item={moneyItem}
        slipItems={moneyCache?.slip ?? []}
        announcementItems={moneyCache?.announcement ?? []}
        ownerUserId={ownerUserId}
        workspaceId={workspaceId}
        onRefresh={refreshMoneyDrawer}
        onItemSynced={setMoneyItem}
      />

      <div
        className={
          activeNav === "data"
            ? isWideRail
              ? "flex min-h-0 min-w-0 flex-1 flex-col max-[899px]:h-auto min-[900px]:overflow-hidden"
              : "flex min-h-full w-full min-w-0 flex-1 flex-col"
            : "hidden"
        }
        aria-hidden={activeNav !== "data"}
      >
        {/* Unmount while on งานเงิน so the legacy table cannot rewrite URL
            (workerId/filters) over Financial Work object deep-links. */}
        {activeNav === "data" ? (
          showAccessDenied ? (
            <FinancialWorkAccessDeniedPanel onRetry={() => void reload()} />
          ) : workMode && queueLoading ? (
            <FinancialWorkQueuePreparing
              workspaceId={workspaceId}
              workspaceName={workspaceLabel}
              serviceId={serviceId}
            />
          ) : (
            dataView
          )
        ) : null}
      </div>

      {activeNav === "sources" ? (
        <div className="flex min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:px-4 sm:py-4 min-[900px]:pb-4">
          <FinancialWorkSourcesPlaceholder />
        </div>
      ) : null}

      {activeNav === "periods" ? (
        <div className="flex min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:px-4 sm:py-4 min-[900px]:pb-4">
          {periodsView ?? (
            <div className="rounded-2xl border border-dashed border-aera-lightgray bg-aera-offwhite px-4 py-5 text-sm text-aera-gray">
              รอบบัญชีจะเปิดเมื่อมีสิทธิ์และฟีเจอร์พร้อมใช้งาน
            </div>
          )}
        </div>
      ) : null}

      {!isDesktop ? (
        <FinancialWorkPrimaryNav
          active={activeNav}
          onChange={setNav}
          showPeriods={showPeriodsNav}
          showSources={showSourcesNav}
          workCount={displayedWorkCount}
          workCountLoading={workCountLoading}
          variant="bottom"
        />
      ) : null}
        </>
      )}
    </>
  );

  return (
    <div
      className={`relative flex min-w-0 gap-0 ${
        !isDesktop && workMode
          ? "min-h-full flex-1 flex-col pb-16"
          : isWideRail
            ? "h-full min-h-0 flex-1 overflow-hidden flex-row rounded-tl-3xl bg-aera-white"
            : isDesktop
              ? "flex min-h-full w-full min-w-0 flex-1 flex-col overflow-visible rounded-tl-2xl border border-aera-lightgray bg-aera-white"
              : "h-full min-h-0 flex-1 overflow-hidden flex-col"
      }`}
      data-testid="fw-shell"
    >
      {isWideRail ? (
        <WorkerSidebar
          mode={workMode ? "work" : "hire"}
          width={288}
          workspaces={workspaces}
          currentWorkspaceId={workspaceId}
          isLoadingWorkspaces={isLoadingWorkspaces}
          selectedServiceId={serviceId || ""}
          selectedWorker={selectedHireWorker}
          setSelectedWorker={handleSelectHireWorker}
          hiredWorkerIds={hiredWorkerIds}
          searchQuery={fwSearch?.searchQuery ?? ""}
          activeTab={activeHireTab}
          onAddWorkspace={handleAddWorkspace}
          onOpenWorkspaceModal={handleOpenWorkspaceSettings}
          onWorkspaceIdChange={onWorkspaceIdChange}
          workNav={
            workMode
              ? {
                  active: activeNav,
                  onChange: setNav,
                  showPeriods: showPeriodsNav,
                  workCount: displayedWorkCount,
                  workCountLoading,
                  showSources: showSourcesNav,
                }
              : undefined
          }
        />
      ) : null}
      <div
        className={
          isWideRail
            ? "flex min-h-0 min-w-0 flex-1 flex-col"
            : "flex min-h-full min-w-0 w-full flex-1 flex-col"
        }
      >
        {mainColumn}
      </div>
    </div>
  );
}
